// Утилиты для генерации и валидации QR-кодов коробов

import CryptoJS from 'crypto-js';
import { v4 as uuidv4 } from 'uuid';
import { BoxCode } from './types';

// Секретный ключ для HMAC (в продакшене должен быть в переменных окружения)
const SECRET_SALT = 'ZERDE_PHARMACY_QR_SECRET_KEY_2025';

/**
 * Генерация HMAC подписи для данных
 */
export const generateHMAC = (data: string, salt: string = SECRET_SALT): string => {
  const hmac = CryptoJS.HmacSHA256(data, salt);
  return hmac.toString(CryptoJS.enc.Hex).substring(0, 16); // Берем первые 16 символов
};

/**
 * Генерация уникального QR-кода для короба
 * Формат: ZRD-{UUID}-{HMAC}
 */
export const generateBoxCode = (docId: string, boxNum: number): string => {
  const uuid = uuidv4();
  const dataToSign = `${docId}-${boxNum}-${uuid}`;
  const hmac = generateHMAC(dataToSign);
  
  return `ZRD-${uuid}-${hmac}`;
};

/**
 * Валидация формата QR-кода
 */
export const validateBoxCodeFormat = (code: string): boolean => {
  // Формат: ZRD-{UUID}-{HMAC}
  const pattern = /^ZRD-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-[0-9a-f]{16}$/i;
  return pattern.test(code);
};

/**
 * Парсинг QR-кода
 */
export const parseBoxCode = (code: string): { uuid: string; hmac: string } | null => {
  if (!validateBoxCodeFormat(code)) {
    return null;
  }

  const parts = code.split('-');
  // ZRD-uuid(5 частей)-hmac
  const uuid = parts.slice(1, 6).join('-'); // части 1-5 это UUID
  const hmac = parts[6]; // последняя часть это HMAC

  return { uuid, hmac };
};

/**
 * Валидация HMAC подписи (упрощенная версия для мока)
 * В реальном приложении проверка должна идти через backend
 */
export const validateBoxCodeHMAC = (code: string, docId: string, boxNum: number): boolean => {
  const parsed = parseBoxCode(code);
  if (!parsed) return false;

  // В моке просто проверяем формат
  // В реальности нужно сверять HMAC с backend
  return true;
};

/**
 * Генерация массива QR-кодов для всех коробов
 */
export const generateBoxCodes = (docId: string, boxCount: number): BoxCode[] => {
  const boxes: BoxCode[] = [];
  
  for (let i = 1; i <= boxCount; i++) {
    boxes.push({
      box_num: i,
      box_code: generateBoxCode(docId, i),
      scanned: false,
      scannedAt: undefined
    });
  }
  
  return boxes;
};

/**
 * Проверка уникальности кода в массиве
 */
export const isCodeUnique = (code: string, boxes: BoxCode[]): boolean => {
  return !boxes.some(box => box.box_code === code);
};

/**
 * Поиск короба по коду
 */
export const findBoxByCode = (code: string, boxes: BoxCode[]): BoxCode | null => {
  return boxes.find(box => box.box_code === code) || null;
};

/**
 * Проверка завершенности сканирования
 */
export const areAllBoxesScanned = (boxes: BoxCode[]): boolean => {
  return boxes.length > 0 && boxes.every(box => box.scanned);
};

/**
 * Получение прогресса сканирования
 */
export const getScanningProgress = (boxes: BoxCode[]): { scanned: number; total: number; percentage: number } => {
  const total = boxes.length;
  const scanned = boxes.filter(box => box.scanned).length;
  const percentage = total > 0 ? Math.round((scanned / total) * 100) : 0;
  
  return { scanned, total, percentage };
};

